import { useCallback, useState } from 'react'

import { useIntl } from 'react-intl'

import type { PurchasingPhase } from '@b-stock/fe-core/bstock-next/helpers'

import { useUnAuthWarningModal } from '@/buyer-portal/components/features/UnAuthWarningModal'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import { ActionFormButton } from '../ActionForm/ActionFormButton'
import { FormRow } from '../ActionForm/ui'
import { useActionPanel } from '../ActionPanelProvider'
import { CTA_ICONS, CTA_TAKES_AN_AMOUNT } from './auctionCta'
import { BidAmountField } from './BidAmountField'
import { getBidFormId } from './getBidFormId'
import { InlineBidOverlay } from './InlineBidOverlay'
import { useAuctionActionForm } from './useAuctionActionForm'

const ns = 'Listings.ListView.actionCell.auction'
const formNs = 'Listings.ListView.actionPanel.form'

/**
 * The auction form. Shared content used both preload and postload.
 *
 * Share UnAuthModal for performance (for multiple actions, per row).
 */
export const AuctionActionCellContent = ({
  activePhase,
  onClickBuyNow,
  searchListing,
}: {
  activePhase: PurchasingPhase | null
  onClickBuyNow: () => void
  searchListing: CombinedListingSearchResult
}) => {
  const { formatMessage } = useIntl()
  const { openUnAuthModal } = useUnAuthWarningModal()
  const iconOnly = useActionPanel().variant === 'narrow'

  const [isOverlayOpen, setIsOverlayOpen] = useState(false)
  const [formRowElement, setFormRowElement] = useState<HTMLDivElement | null>(
    null
  )

  // Bid overlay anchors to the cell. Falls back to form row for tests.
  const anchorElement =
    formRowElement?.closest<HTMLElement>('.ag-cell') ?? formRowElement

  const form = useAuctionActionForm({
    activePhase,
    onClickBuyNow,
    onUnauthenticated: openUnAuthModal,
    searchListing,
  })

  const { cta, onClickAction, resetValue } = form

  const takesAnAmount = CTA_TAKES_AN_AMOUNT[cta]
  const opensOverlay = iconOnly && takesAnAmount

  const onDismiss = useCallback(() => {
    setIsOverlayOpen(false)
    resetValue()
  }, [resetValue])

  const onSubmitted = useCallback(() => setIsOverlayOpen(false), [])

  return (
    <FormRow ref={setFormRowElement}>
      {!iconOnly && takesAnAmount && <BidAmountField form={form} />}

      <ActionFormButton
        description={formatMessage({ id: `${formNs}.description.${cta}` })}
        expanded={opensOverlay ? isOverlayOpen : undefined}
        icon={CTA_ICONS[cta]}
        iconOnly={iconOnly}
        label={formatMessage({ id: `${ns}.cta.${cta}` })}
        onClick={
          opensOverlay
            ? () => (isOverlayOpen ? onDismiss() : setIsOverlayOpen(true))
            : onClickAction
        }
        type={opensOverlay ? 'button' : 'submit'}
      />

      {opensOverlay && isOverlayOpen && (
        <InlineBidOverlay
          anchorElement={anchorElement}
          form={form}
          formId={getBidFormId(searchListing.listingId)}
          onDismiss={onDismiss}
          onSubmitted={onSubmitted}
        />
      )}
    </FormRow>
  )
}
